/**
 * PlanSummaryArea Component
 *
 * Purpose: Displays placeholder summary cards above the plan list table.
 *          Real chart implementations come in Phase 2 (ORCH-118).
 * Dependencies: React, Material-UI
 * Author: Development Team
 * Created: 2026-04-01
 */

import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AccountBalance as BudgetIcon,
  Assessment as PerformanceIcon,
  PieChart as MediaMixIcon,
  TrendingUp as TopPerformingIcon,
} from '@mui/icons-material';

const PlaceholderCard = styled(Card)(({ theme }) => ({
  height: 100,
  display: 'flex',
  alignItems: 'center',
  border: `2px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
}));

interface SummaryCard {
  title: string;
  icon: React.ReactElement;
}

const summaryCards: SummaryCard[] = [
  { title: 'Budget Pacing', icon: <BudgetIcon color="primary" /> },
  { title: 'Performance KPIs', icon: <PerformanceIcon color="secondary" /> },
  { title: 'Media Mix', icon: <MediaMixIcon color="info" /> },
  { title: 'Top Performing', icon: <TopPerformingIcon color="success" /> },
];

const PlanSummaryArea: React.FC = () => {
  return (
    <Box sx={{ mb: 3 }} data-testid="plan-summary-area">
      <Grid container spacing={2}>
        {summaryCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.title}>
            <PlaceholderCard>
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  py: 1.5,
                  '&:last-child': { pb: 1.5 },
                }}
              >
                {card.icon}
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {card.title}
                  </Typography>
                  <Chip
                    label="Coming Soon"
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
              </CardContent>
            </PlaceholderCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PlanSummaryArea;
